import {
    type ApiResponse,
    BackendClient,
    type BackendClientParameters,
} from "@internal/backstage-plugin-utility-common";

import type { IncidentCardData, OnCallCardData } from "../types";

/**
 * Parameters for creating an {@link IncidentClient}.
 *
 * @public
 */
export type IncidentClientOptions = Omit<
    BackendClientParameters,
    "targetPluginId"
>;

/**
 * How the incident.io cards reach incident.io: through the `incidentio` backend,
 * never directly.
 *
 * The API key lives on the backend, so the browser cannot make an incident.io
 * request of its own choosing. What it can ask for is one entity's card, and the
 * backend decides which queries that means.
 *
 * @public
 *
 * @example
 * ```typescript
 * const client = new IncidentClient({ discoveryApi, identityApi });
 * const res = await client.getCard("component:default/backstage");
 * if (res.body.ok) render(res.body.data);
 * ```
 */
export class IncidentClient extends BackendClient {
    constructor(options: IncidentClientOptions) {
        super({
            discoveryApi: options.discoveryApi,
            identityApi: options.identityApi,
            targetPluginId: "incidentio",
        });
    }

    /**
     * Everything one entity's card renders: the dashboard base URL, who is
     * on-call, and the incidents and alerts columns.
     *
     * One request, where the browser used to make six to eight. The union across
     * filter legs, the schedule expansion and the org constants are all joined
     * server-side now, so a page of cards costs a request each rather than a
     * request each per leg.
     *
     * Never rejects for a partial answer. Each column and the on-call half carry
     * their own `failed`, so an incidents outage still returns alerts and the
     * card can say which half it could not load.
     *
     * @param entityRef - Stringified entity ref, e.g. `component:default/backstage`.
     */
    public async getCard(
        entityRef: string
    ): Promise<ApiResponse<IncidentCardData>> {
        return this.api<IncidentCardData>(
            `/cards/${encodeURIComponent(entityRef)}`
        );
    }

    /**
     * Who is on-call for one entity, without the incidents and alerts columns.
     *
     * For the home widget, which renders only this half and reloads it on every
     * team switch. `getCard` would answer the same question and pay for two
     * columns nobody draws.
     *
     * @param entityRef - Stringified entity ref, e.g. `group:default/nebula`.
     */
    public async getOnCall(
        entityRef: string
    ): Promise<ApiResponse<OnCallCardData>> {
        return this.api<OnCallCardData>(
            `/oncall/${encodeURIComponent(entityRef)}`
        );
    }
}
